import { ChevronRight, Menu } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Theme, useTheme } from '@mui/material/styles';
import AppleIcon from '/public/icons/common/AppleIcon.svg';
import FacebookIcon from '/public/icons/common/facebook.svg';
import InstagramIcon from '/public/icons/common/instagram.svg';
import PlayIcon from '/public/icons/common/play-icon.svg';
import MenuSvg from '/public/icons/menu.svg';

import TwitterIcon from '@mui/icons-material/Twitter';
import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  Link as MUILink,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Cookies from 'js-cookie';
import Link from 'next/link';
import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import {
  UserCurrentQuery,
  useUserCurrentQuery,
} from '../redux/services/auth/auth.generated';
import RagyaLogo from '/public/icons/ragya-logo.svg';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { praharTextColorSelector } from '../redux/slices/praharSlice';

interface SideBarProps {
  breakpoint: boolean;
  activeTab: string;
  praharColor: string;
  userData: UserCurrentQuery | undefined;
}

const StaticPageSideBar: FunctionComponent<SideBarProps> = ({ breakpoint }) => {
  const router = useRouter();
  const currentPath = router.asPath.split('?')[0];
  const isGoPremium = currentPath === '/go-premium';

  const praharTextColor = useSelector(praharTextColorSelector);
  const { asPath } = useRouter();
  const isPraharPage = asPath.includes('/prahar');
  const praharTextColorValue = isPraharPage ? praharTextColor : '#fff';

  const isSpecialTab =
    currentPath === '/prahar' ||
    currentPath === '/browse' ||
    currentPath === '/favourites';

  //state
  const [state, setState] = useState({
    left: false,
  });
  const theme = useTheme();
  const styles = useStyles(theme);
  const mainMenu = [
    {
      path: '/go-premium',
      name: 'Subscription Benefits',
    },
    {
      path: '/gift',
      name: 'Gift Ragya',
    },
  ];

  const userToken = Cookies.get('ragya-token');

  const { data: userData } = useUserCurrentQuery({}, { skip: !userToken });

  const subMenu = [
    // {
    //   path: '/redeem',
    //   name: 'Redeem a gift coupon',
    // },
    {
      path: '/refunds-and-cancellations',
      name: 'Refunds and Cancellations',
    },
    {
      path: '/terms-and-conditions',
      name: 'Terms of Use',
    },
    {
      path: '/privacy-policy',
      name: 'Privacy Policy',
    },
    {
      path: '/disclaimer',
      name: 'Disclaimer',
    },
    {
      path: 'mailto:support@ragya.com',
      name: 'Report a Problem',
      newPage: true,
    },
  ];

  const platformList = [
    {
      name: 'App Store',
      downloadText: 'Download on the',
      icon: <AppleIcon size={30} />,
      url: 'https://apps.apple.com/in/app/ragya/id1481987704',
    },
    {
      name: 'Play Store',
      downloadText: 'Download on the',
      //note: Apple icon has padding in figma, so have to give padding to match the design
      icon: <PlayIcon style={styles.playIcon} />,
      url: 'https://play.google.com/store/apps/details?id=com.ragya',
    },
    {
      name: 'Mac App',
      downloadText: 'Download the',
      icon: <AppleIcon size={30} />,
      //fixme: replace link with mac app link
      url: 'https://ragya-static-assets.s3.ap-south-1.amazonaws.com/apps/Ragya-1.0.2.dmg',
    },
  ];

  const socialMediaProfiles = [
    { url: 'https://www.facebook.com/ragyamusic', icon: <FacebookIcon /> },
    {
      url: 'https://twitter.com/ragyamusic',
      icon: <TwitterIcon sx={styles.twitterIcon} />,
    },
    { url: 'https://www.instagram.com/ragyamusic/', icon: <InstagramIcon /> },
  ];

  const toggleDrawer =
    (anchor: 'right' | 'left', open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setState({ ...state, [anchor]: open });
    };

  return (
    <React.Fragment key='right'>
      <IconButton
        onClick={toggleDrawer('left', true)}
        sx={{
          p: isSpecialTab && !breakpoint ? '10px' : 'none',
          // keep your existing margin-left only on mobile
          ml: !breakpoint ? '6px' : '',

          // mirror your transparent/blur logic
          backgroundColor:
            isSpecialTab && !breakpoint
              ? 'rgba(255,255,255,0.01)'
              : 'transparent', // your “else” default

          backdropFilter: isSpecialTab && !breakpoint ? 'blur(15px)' : 'none', // your “else” default
        }}
      >
        <Menu
          sx={{
            color: isGoPremium
              ? (theme) => theme.ragyaPalette.gray8
              : praharTextColorValue,
            textShadow: '0px 1px 3px rgba(0,0,0,0.5)',
          }}
        />
      </IconButton>
      <SwipeableDrawer
        anchor='left'
        open={state['left']}
        onClose={toggleDrawer('left', false)}
        onOpen={toggleDrawer('left', true)}
      >
        <Box
          sx={{
            ...styles.drawerContainer,
            width: breakpoint ? '400px' : '100vw',
            paddingBottom: breakpoint ? '75px' : '',
          }}
          role='presentation'
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: 1,
            }}
          >
            <RagyaLogo fill={theme.ragyaPalette.gray4} width={80} height={40} />
            <IconButton
              sx={{
                m: 1,
                color: (theme) => theme.ragyaPalette.gray4,
              }}
              onClick={toggleDrawer('left', false)}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          {/* Sign Up button for mobile view */}
          {/* {!breakpoint && !userData && (
            <>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ pl: 0, pb: 1 }}>
                  <Link href='/login' passHref>
                    <MUILink
                      variant='body2'
                      fontWeight={500}
                      sx={styles.mainMenuItem}
                      style={{ width: '100%' }}
                    >
                      Sign In
                    </MUILink>
                  </Link>
                  <ChevronRight sx={{ color: theme.ragyaPalette.gray4 }} />
                </ListItem>
              </List>
              <Divider sx={{ my: 1 }} />
            </>
          )} */}
          <List sx={{ pb: 0 }}>
            {mainMenu.map((route) => (
              <ListItem key={route.path} sx={{ pl: 0, pb: 0 }}>
                <Link href={route.path} key={route.path} passHref>
                  <MUILink
                    target='_blank'
                    variant={'body2'}
                    fontWeight={500}
                    sx={styles.mainMenuItem}
                  >
                    {route.name}
                  </MUILink>
                </Link>
                <ChevronRight sx={{ color: theme.ragyaPalette.gray4 }} />
              </ListItem>
            ))}
          </List>
          <List>
            {!userData?.userCurrent.activeSubscription?.isActive && (
              <ListItem sx={{ pb: 0, pl: 0 }}>
                <Link href={'/redeem'} passHref>
                  <MUILink
                    variant={'subtitle1'}
                    fontWeight={500}
                    sx={styles.subMenuitem}
                    target={'_blank'}
                  >
                    Redeem a gift coupon
                  </MUILink>
                </Link>
              </ListItem>
            )}
            {subMenu.map((route) => (
              <ListItem key={route.path} sx={{ pb: 0, pl: 0 }}>
                {route.newPage ? (
                  <MUILink
                    variant={'subtitle1'}
                    fontWeight={500}
                    sx={styles.subMenuitem}
                    target={'_blank'}
                    href={route.path}
                  >
                    {route.name}
                  </MUILink>
                ) : (
                  <Link href={route.path} key={route.path} passHref>
                    <MUILink
                      variant={'subtitle1'}
                      fontWeight={500}
                      sx={styles.subMenuitem}
                      target={route.newPage ? '_blank' : '_self'}
                    >
                      {route.name}
                    </MUILink>
                  </Link>
                )}
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant={'subtitle1'}
            letterSpacing={0.6}
            sx={styles.contactusHeader}
          >
            Are you an artiste or organisation looking to add music on Ragya?
          </Typography>

          <Link href={'/for-artists'} passHref>
            <MUILink
              target='_blank'
              onClick={(e) => e.stopPropagation()}
              variant={'subtitle1'}
              fontWeight={700}
              letterSpacing={0.6}
              sx={styles.contactsLink}
            >
              Learn more
            </MUILink>
          </Link>
          <Divider sx={{ my: 1 }} />
          <Box>
            <Typography
              variant={'subtitle2'}
              sx={styles.downloadAppText}
              gutterBottom
            >
              DOWNLOAD THE APP(S)
            </Typography>
            <Grid container spacing={2}>
              {platformList?.map(({ name, url, icon, downloadText }) => {
                return (
                  <Grid item xs={6} key={url}>
                    <MUILink
                      target='_blank'
                      rel='noopener'
                      href={url}
                      style={{ textDecoration: 'none' }}
                    >
                      <Box sx={styles.downloadBox}>
                        <Box sx={{ display: 'flex' }}>
                          <Box sx={styles.appLink}>{icon}</Box>
                          <Box
                            sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box>
                              <Typography
                                variant={'h4'}
                                fontSize={10}
                                sx={styles.innerDownloadText}
                                gutterBottom
                              >
                                {downloadText}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant='h2'
                                fontWeight={600}
                                sx={styles.storeNameText}
                                gutterBottom
                              >
                                {name}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </MUILink>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          <Divider sx={{ py: 1 }} />
          <Typography
            variant={'subtitle2'}
            fontWeight={700}
            letterSpacing={1.4}
            sx={styles.socialMediaHeader}
          >
            CONNECT WITH US
          </Typography>
          <Box>
            {socialMediaProfiles.map((smp) => (
              <MUILink
                key={smp.url}
                target='_blank'
                rel='noopener'
                href={smp.url}
                sx={styles.socialMediaLink}
              >
                {smp.icon}
              </MUILink>
            ))}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant='body2'
            fontWeight={300}
            letterSpacing={0.4}
            sx={styles.footer}
          >
            Made with ❤️ and for the ❤️ of hindustani classical music️, in India
          </Typography>
        </Box>
      </SwipeableDrawer>
    </React.Fragment>
  );
};

export default StaticPageSideBar;
const useStyles = (theme: Theme) => ({
  drawerContainer: {
    p: 3,
    paddingTop: 0,
    flex: 1,
    backgroundColor: theme.ragyaPalette.gray12,
  },
  drawerHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    my: 1,
  },
  mainMenuItem: {
    flex: 1,
    color: theme.ragyaPalette.gray0,
    textDecoration: 'none',
    transition: '0.2s all',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  subMenuitem: {
    color: theme.ragyaPalette.gray3,
    textDecoration: 'none',
    '&:hover': {
      color: theme.ragyaPalette.gray0,
    },
  },
  twitterIcon: { width: 32, height: 32, color: theme.ragyaPalette.gray4 },
  playIcon: { marginRight: 4, marginLeft: 4 },
  downloadAppText: { my: 2, color: theme.ragyaPalette.gray6 },
  downloadBox: {
    pt: 1,
    pb: 0.5,
    pl: 0.5,
    border: `1px solid ${theme.ragyaPalette.gray6}`,
    borderRadius: '7px',
    '&:hover': {
      opacity: 0.8,
    },
  },
  appLink: { mx: 1, color: 'white', alignSelf: 'center' },
  innerDownloadContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  innerDownloadText: {
    color: theme.ragyaPalette.gray4,
    letterSpacing: '0.3px',
  },
  storeNameText: {
    color: theme.ragyaPalette.gray4,
    letterSpacing: '0.17px',
  },
  socialMediaHeader: { my: 2, color: theme.ragyaPalette.gray4 },
  footer: { my: 2, color: theme.ragyaPalette.gray5 },
  contactusHeader: {
    mt: 2,
    mb: 1,
    color: theme.ragyaPalette.gray4,
    cursor: 'pointer',
  },
  contactsLink: {
    mb: 2,
    color: theme.ragyaPalette.gray4,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  socialMediaLink: { mr: 1 },
});
